// Topbar dropdown that shows the user's current workspace + lets them
// switch / create / open management. Calls /api/auth/me to discover
// memberships; super-admin gets a "Show all workspaces" toggle.
//
// Renders nothing when /api/auth/me returns null (un-authed) — used
// inside authed pages, the AuthGate already gates access.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMe, listWorkspaces, selectWorkspace } from '../api';
import type { UserPublic } from '@datahub/shared';
import type { WorkspaceWithRole } from '../api';

export default function WorkspaceSwitcher(): React.ReactElement | null {
  const [me, setMe] = useState<UserPublic | null | undefined>(undefined);
  const [workspaces, setWorkspaces] = useState<WorkspaceWithRole[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!me) return;
    listWorkspaces(false).then(setWorkspaces).catch(() => setWorkspaces([]));
  }, [me?.user_id]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!me) return null;

  const active = workspaces.find(w => w.workspace_id === me.current_workspace_id);
  const label = active ? active.name : (workspaces.length > 0 ? 'Pick workspace' : 'No workspace');

  async function onSelect(ws: WorkspaceWithRole): Promise<void> {
    try {
      await selectWorkspace(ws.workspace_id);
      // Hard reload so server-side workspace_id resolution picks up
      // the new cookie immediately (sources list, etc.).
      window.location.reload();
    } catch {
      // best-effort
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', fontSize: 11.5, fontFamily: 'var(--mono)',
          background: 'var(--bg-elev)', color: 'var(--ink)',
          border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer',
        }}
        title="Switch workspace"
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: active ? 'oklch(0.65 0.18 145)' : 'var(--ink-3)' }} />
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ color: 'var(--ink-3)' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)',
          minWidth: 240, maxWidth: 320,
          background: 'var(--bg)', border: '1px solid var(--line)',
          borderRadius: 6, boxShadow: '0 4px 18px oklch(0.10 0 0 / 0.18)',
          zIndex: 10, overflow: 'hidden',
        }}>
          {workspaces.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-2)' }}>
              You're not in any workspace yet.
            </div>
          )}
          {workspaces.map(ws => (
            <button
              key={ws.workspace_id}
              onClick={() => onSelect(ws)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 14px', fontSize: 12,
                background: ws.workspace_id === me.current_workspace_id ? 'var(--bg-elev)' : 'transparent',
                color: 'var(--ink)', border: 'none', cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 500 }}>{ws.name}</div>
              <div style={{ fontSize: 10.5, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
                {ws.role} · {ws.workspace_id}
              </div>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--line)' }}>
            <Link
              to="/workspaces"
              onClick={() => setOpen(false)}
              style={{
                display: 'block', padding: '8px 14px', fontSize: 12,
                color: 'var(--ink-2)', textDecoration: 'none',
              }}
            >
              Manage workspaces ↗
            </Link>
            {me.is_super_admin && (
              <Link
                to="/audit"
                onClick={() => setOpen(false)}
                style={{
                  display: 'block', padding: '8px 14px', fontSize: 12,
                  color: 'var(--ink-2)', textDecoration: 'none', borderTop: '1px solid var(--line)',
                }}
              >
                Audit log ↗
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
